import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { Cms1500 } from '@/types/encounter';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const cms1500Repository = {
  async getCms1500ById({
    host,
    cms1500ID
  }: {
    host: string;
    cms1500ID: string;
  }): Promise<RepositoryResponse<Cms1500>> {
    try {
      const path = `/api/billing/cms1500/${cms1500ID}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get CMS1500 by ID.');
    }
  },

  async getCms1500ByHealthieId({
    host,
    cms1500ID
  }: {
    host: string;
    cms1500ID: string;
  }): Promise<RepositoryResponse<Cms1500>> {
    try {
      const path = `/api/billing/cms1500/by_healthie_id/${cms1500ID}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get CMS1500 by Healthie ID.');
    }
  },

  async getCms1500ByExternalId({
    host,
    externalId
  }: {
    host: string;
    externalId: string;
  }): Promise<RepositoryResponse<Cms1500>> {
    try {
      const path = `/api/billing/cms1500/by_external_id/${externalId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get CMS1500 by External ID.');
    }
  },

  async getCms1500ByCandidEncounterId({
    host,
    candidEncounterId
  }: {
    host: string;
    candidEncounterId: string;
  }): Promise<RepositoryResponse<Cms1500>> {
    try {
      const path = `/api/billing/cms1500/by_candid_encounter_id/${candidEncounterId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to get CMS1500 by Candid encounter ID.'
      );
    }
  }
};

const Cms1500Repository = () => {
  const { host } = useContext(AppContext);

  return {
    async getCms1500ById({
      cms1500ID
    }: {
      cms1500ID: string;
    }): Promise<RepositoryResponse<Cms1500>> {
      return cms1500Repository.getCms1500ById({ host, cms1500ID });
    },

    async getCms1500ByHealthieId({
      cms1500ID
    }: {
      cms1500ID: string;
    }): Promise<RepositoryResponse<Cms1500>> {
      return cms1500Repository.getCms1500ByHealthieId({ host, cms1500ID });
    },

    async getCms1500ByExternalId({
      externalId
    }: {
      externalId: string;
    }): Promise<RepositoryResponse<Cms1500>> {
      return cms1500Repository.getCms1500ByExternalId({ host, externalId });
    },

    async getCms1500ByCandidEncounterId({
      candidEncounterId
    }: {
      candidEncounterId: string;
    }): Promise<RepositoryResponse<Cms1500>> {
      return cms1500Repository.getCms1500ByCandidEncounterId({ host, candidEncounterId });
    }
  };
};

export default Cms1500Repository;
